import React from 'react';
import ReactDOM from 'react-dom';
import WindowMoveLocker from './WindowMoveLocker';

// Simple abstraction for dragging events names.
const eventsFor = {
    touch: {
        start: 'touchstart',
        move: 'touchmove',
        stop: 'touchend'
    },
    mouse: {
        start: 'mousedown',
        move: 'mousemove',
        stop: 'mouseup'
    }
};

// Default to mouse events.
let dragEventFor = eventsFor.mouse;

class Draggable extends React.Component {
    // To add new event
    addEvent = (el, event, handler) => {
        if (!el) { return; }
        if (el.attachEvent) {
            el.attachEvent('on' + event, handler);
        } else if (el.addEventListener) {
            if (event === 'touchmove') {
                el.addEventListener(event, handler, { passive: false });
            } else {
                el.addEventListener(event, handler);
            }
        } else {
            el['on' + event] = handler;
        }
    }

    // To remove events
    removeEvent = (el, event, handler) => {
        if (!el) { return; }
        if (el.detachEvent) {
            el.detachEvent('on' + event, handler);
        } else if (el.removeEventListener) {
            if (event === 'touchmove') {
                el.removeEventListener(event, handler, { passive: false });
            } else {
                el.removeEventListener(event, handler);
            }
        } else {
            el['on' + event] = null;
        }
    }

    // To get position
    getPosition = (e) => {
        if (e.type.indexOf('touch') === 0) {
            const touch = (e.type === eventsFor.touch.stop ? e.changedTouches[0] : e.touches[0]);
            return {
                x: touch.clientX,
                y: touch.clientY
            }
        } else {
            return {
                x: e.clientX,
                y: e.clientY
            }
        }
    }

    // Drag specific events
    handleDragStart = (e) => {
        var isTouchEvent = e.type.indexOf('touch') === 0;
        // PreventDefault for non-touch devices
        if(!isTouchEvent) {
            e.preventDefault();
        }
        // Only accept left-clicks.
        if (typeof e.button === 'number' && e.button !== 0) {
            return false;
        }

        var position = this.getPosition(e);
        if (isTouchEvent) {
            this.dragDelayTimer = setTimeout(() => {
                this.dragDelayTimer = null;

                WindowMoveLocker.preventTouchMove();
                this.isDragging = true;
                this.props.onStart(e, position);
            }, 150);
        } else {
            this.isDragging = true;
            this.props.onStart(e, position);
        }

        // To bind events
        const ownerDocument = this.getOwnerDoc();
        this.addEvent(ownerDocument, dragEventFor.move, this.handleDrag);
        this.addEvent(ownerDocument, dragEventFor.stop, this.handleDragStop);
    }

    handleDrag = (e) => {
        if (e.type === 'touchmove') {
            // Cancel the drag event when dragDelayTimer is not end yet
            if (this.dragDelayTimer) {
                clearTimeout(this.dragDelayTimer);
                this.handleDragStop(e);
                return;
            } else {
                // Prevent scrolling on mobile devices, like ipad/iphone.
                e.preventDefault();
            }
        }
        this.props.onDrag(e, this.getPosition(e));
    }

    handleDragStop = (e) => {
        // Cancel the timer
        if (e.type === 'touchend' && this.dragDelayTimer) {
            clearTimeout(this.dragDelayTimer);
        }
        if (!this.isDragging) {
            return;
        }
        this.isDragging = false;
        WindowMoveLocker.releaseTouchMove();
        const position = this.getPosition(e);
        this.props.onStop(e, position);

        // Remove binded events
        const ownerDocument = this.getOwnerDoc();
        this.removeEvent(ownerDocument, dragEventFor.move, this.handleDrag);
        this.removeEvent(ownerDocument, dragEventFor.stop, this.handleDragStop);
    }

    // Get document root reference. Be sure to grab relative document (could be iframed)
    getOwnerDoc = () => {
        const thisNode = ReactDOM.findDOMNode(this);
        return thisNode ? thisNode.ownerDocument.defaultView : window;
    }

    // Core Events
    onMouseDown = (e) => {
        dragEventFor = eventsFor.mouse; // on touchscreen laptops we could switch back to mouse
        return this.handleDragStart(e);
    }

    onMouseUp = (e) => {
        dragEventFor = eventsFor.mouse;
        return this.handleDragStop(e);
    }

    onTouchStart = (e) => {
        // We're on a touch device now, so change the event handlers
        dragEventFor = eventsFor.touch;
        return this.handleDragStart(e);
    }

    onTouchEnd = (e) => {
        dragEventFor = eventsFor.touch;
        return this.handleDragStop(e);
    }

    render = () => {
        return React.cloneElement(React.Children.only(this.props.children), {
            style: this.props.children.props.style,
            onMouseDown: this.onMouseDown,
            onMouseUp: this.onMouseUp,
            onTouchStart: this.onTouchStart,
            onTouchEnd: this.onTouchEnd
        })
    }
}

export default Draggable;
